/**
 * MobileBottomTabs Component
 *
 * Fixed bottom tab bar for mobile navigation (<768px):
 * - 5-item maximum with "More" overflow
 * - Touch-friendly 44×44px minimum tap targets
 * - Safe area padding for notched devices
 * - Badge support for unread counts
 * - Bottom sheet for overflow items
 *
 * Epic: MVP 1.21.2 - Dashboard Design System & Notifications (SCRUM-459)
 * Component: US-1.21.2-02 - Mobile Bottom Tab Navigation
 *
 * @component
 * @param {Object} props
 * @param {Array} props.items - Navigation items to display (max 5)
 * @param {string} props.activeId - Currently active navigation item ID
 * @param {Function} props.onNavigate - Navigation callback function
 * @param {Array} props.allItems - All navigation items (for "More" overflow)
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { X } from 'lucide-react';
import NotificationBadge from '../common/NotificationBadge';

const MobileBottomTabs = ({
  items,
  activeId,
  onNavigate,
  allItems = []
}) => {
  const [showMore, setShowMore] = useState(false);

  const handleItemClick = (item) => {
    if (item.isMore) {
      setShowMore(true);
      return;
    }

    if (onNavigate) {
      onNavigate(item.id, item.path);
    }
    setShowMore(false);
  };

  // Get overflow items (items not in the bottom bar)
  const getOverflowItems = () => {
    const bottomBarIds = items.filter(item => !item.isMore).map(item => item.id);

    // For flat navigation (guide/customer)
    if (Array.isArray(allItems) && allItems.length > 0 && !allItems[0].category) {
      return allItems.filter(item => !bottomBarIds.includes(item.id));
    }

    // For accordion navigation (admin)
    if (Array.isArray(allItems) && allItems.length > 0 && allItems[0].category) {
      return allItems.flatMap(category =>
        category.items.filter(item => !bottomBarIds.includes(item.id))
      );
    }

    return [];
  };

  const overflowItems = getOverflowItems();

  return (
    <>
      {/* Bottom Tab Bar */}
      <div style={styles.tabBar}>
        <div style={styles.tabBarInner}>
          {items.map((item) => {
            const Icon = item.icon;
            const isActive = item.id === activeId;

            return (
              <button
                key={item.id}
                onClick={() => handleItemClick(item)}
                style={{
                  ...styles.tabItem,
                  ...(isActive && styles.tabItemActive),
                }}
              >
                <div style={styles.iconContainer}>
                  <Icon
                    size={24}
                    style={{
                      color: isActive
                        ? 'var(--color-primary)'
                        : 'var(--color-gray-500)',
                    }}
                  />
                  {item.badge && item.badge > 0 && (
                    <div style={styles.badge}>
                      {item.badge > 99 ? '99+' : item.badge}
                    </div>
                  )}
                </div>
                <span
                  style={{
                    ...styles.label,
                    color: isActive
                      ? 'var(--color-primary)'
                      : 'var(--color-text-secondary)',
                  }}
                >
                  {item.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* More Bottom Sheet */}
      {showMore && (
        <>
          {/* Backdrop */}
          <div
            style={styles.backdrop}
            onClick={() => setShowMore(false)}
          />

          {/* Bottom Sheet */}
          <div style={styles.bottomSheet}>
            <div style={styles.sheetHeader}>
              <h3 style={styles.sheetTitle}>More</h3>
              <button
                onClick={() => setShowMore(false)}
                style={styles.closeButton}
              >
                <X size={24} color="var(--color-gray-700)" />
              </button>
            </div>

            <div style={styles.sheetContent}>
              {overflowItems.map((item) => {
                const Icon = item.icon;
                const isActive = item.id === activeId;

                return (
                  <button
                    key={item.id}
                    onClick={() => handleItemClick(item)}
                    style={{
                      ...styles.sheetItem,
                      ...(isActive && styles.sheetItemActive),
                    }}
                  >
                    <Icon
                      size={20}
                      style={{
                        color: isActive
                          ? 'var(--color-primary)'
                          : 'var(--color-gray-500)',
                      }}
                    />
                    <span style={styles.sheetItemLabel}>{item.label}</span>
                    {item.badge && item.badge > 0 && (
                      <NotificationBadge count={item.badge} small />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </>
  );
};

const styles = {
  tabBar: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    height: 'var(--bottom-tab-height)',
    backgroundColor: 'var(--color-white)',
    borderTop: '1px solid var(--color-border)',
    boxShadow: 'var(--shadow-sm)',
    zIndex: 'var(--z-fixed)',
    paddingBottom: 'env(safe-area-inset-bottom)', // Safe area for notched devices
  },

  tabBarInner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-around',
    height: '100%',
    maxWidth: '100%',
    padding: '0 var(--space-2)',
  },

  tabItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'var(--space-1)',
    minWidth: 'var(--bottom-tab-item-size)',
    minHeight: 'var(--bottom-tab-item-size)',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: 'var(--space-1)',
    borderRadius: 'var(--radius-sm)',
    transition: 'background-color var(--transition-fast)',
  },

  tabItemActive: {
    backgroundColor: 'var(--color-primary-light)',
  },

  iconContainer: {
    position: 'relative',
  },

  badge: {
    position: 'absolute',
    top: '-4px',
    right: '-8px',
    minWidth: '16px',
    height: '16px',
    borderRadius: 'var(--radius-full)',
    backgroundColor: 'var(--color-error)',
    color: 'var(--color-white)',
    fontSize: '10px',
    fontWeight: 'var(--font-weight-bold)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 4px',
  },

  label: {
    fontSize: 'var(--font-size-xs)',
    fontWeight: 'var(--font-weight-medium)',
    lineHeight: 'var(--line-height-tight)',
  },

  backdrop: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 'var(--z-modal-backdrop)',
  },

  bottomSheet: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    maxHeight: '80vh',
    backgroundColor: 'var(--color-white)',
    borderTopLeftRadius: 'var(--radius-xl)',
    borderTopRightRadius: 'var(--radius-xl)',
    boxShadow: 'var(--shadow-xl)',
    zIndex: 'var(--z-modal)',
    paddingBottom: 'calc(var(--bottom-tab-height) + env(safe-area-inset-bottom))',
    animation: 'slideUp 0.3s ease-out',
  },

  sheetHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 'var(--space-4) var(--space-6)',
    borderBottom: '1px solid var(--color-border)',
  },

  sheetTitle: {
    fontSize: 'var(--font-size-lg)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-primary)',
    margin: 0,
  },

  closeButton: {
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: 'var(--space-2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 'var(--radius-sm)',
    transition: 'background-color var(--transition-fast)',
  },

  sheetContent: {
    padding: 'var(--space-4) var(--space-2)',
    overflowY: 'auto',
    maxHeight: 'calc(80vh - 60px - var(--bottom-tab-height))',
  },

  sheetItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-3)',
    width: '100%',
    padding: 'var(--space-3) var(--space-4)',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: 'var(--radius-sm)',
    cursor: 'pointer',
    transition: 'background-color var(--transition-fast)',
    textAlign: 'left',
  },

  sheetItemActive: {
    backgroundColor: 'var(--color-primary-light)',
  },

  sheetItemLabel: {
    fontSize: 'var(--font-size-base)',
    color: 'var(--color-text-primary)',
    flex: 1,
  },
};

// Add slideUp animation keyframes
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = `
    @keyframes slideUp {
      from {
        transform: translateY(100%);
      }
      to {
        transform: translateY(0);
      }
    }
  `;
  document.head.appendChild(styleSheet);
}

MobileBottomTabs.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.elementType.isRequired,
      path: PropTypes.string,
      badge: PropTypes.number,
      isMore: PropTypes.bool,
    })
  ).isRequired,
  activeId: PropTypes.string,
  onNavigate: PropTypes.func,
  allItems: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.object),
    PropTypes.arrayOf(
      PropTypes.shape({
        category: PropTypes.string,
        items: PropTypes.arrayOf(PropTypes.object),
      })
    ),
  ]),
};

export default MobileBottomTabs;
